import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Globe2,
  Users,
  TrendingUp,
  MessagesSquare,
  Search,
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  UserRound,
  Flag,
  Hand,
  MapPin,
  Megaphone,
  ShieldAlert,
  Clock,
  CheckCircle2,
  NotebookPen,
  ExternalLink,
  FileText,
} from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";

// =============================================================================
// Marketing Analytics → Chats
// =============================================================================
// Conversations from the AI chat on the five Spinhouse landings, read as
// dialogues: list · conversation · case file (lead, traffic source, judge).
// Landings & Leads shows raw log rows, where one conversation is scattered
// across many lines; this page groups them by (landing, session).
//
// Data: GET /landing-leads/chats and /landing-leads/chats/{landing}/{session}
// (backend/landing_chats.py) over db.landing_leads — the same 5-minute mirror
// of the landing logs. Judge verdicts arrive as JUDGE_VERDICT rows written by
// chat_judge.php in spinhouse-landings.
//
// "Take over" is a placeholder for step two (live takeover via Telegram).

const LANDINGS = [
  { key: "all", label: "All" },
  { key: "kz", label: "KZ" },
  { key: "ua", label: "UA" },
  { key: "tr", label: "TR" },
  { key: "uz", label: "UZ" },
  { key: "en", label: "EN" },
];

const LANDING_HOST = {
  tr: "landing.spinhouse.ai",
  ua: "ua.spinhouse.ai",
  uz: "uz.spinhouse.ai",
  kz: "kz.spinhouse.ai",
  en: "landing.spinhouse.ai/en",
};

const ROLE_LABEL = {
  developer: "Developer",
  agency: "Agency",
  studio: "Studio",
  buyer: "Private buyer",
  other: "Other",
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Log times are "YYYY-MM-DD HH:MM:SS" in UTC (the landing server clock).
function parseLogTime(s) {
  if (!s) return null;
  const d = new Date(s.replace(" ", "T") + "Z");
  return isNaN(d.getTime()) ? null : d;
}

function fmtShort(s) {
  const d = parseLogTime(s);
  if (!d) return "";
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

function fmtFull(s) {
  const d = parseLogTime(s);
  if (!d) return "";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}, ${hh}:${mm} UTC`;
}

function fmtDuration(from, to) {
  const a = parseLogTime(from);
  const b = parseLogTime(to);
  if (!a || !b) return "";
  const sec = Math.max(0, Math.round((b - a) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${min % 60}m`;
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function whoOf(c) {
  if (c?.lead?.name) return c.lead.name;
  const city = (c?.geo || "").split(",")[0].trim();
  return city ? `${city} visitor` : "Visitor";
}

// A judge quote is a verbatim (or lower-cased, for the repeat rule) piece of a
// bot answer. Match loosely so the flag lands on the reply it's about.
function norm(s) {
  return (s || "").toLowerCase().replace(/\s+/g, " ").trim();
}
function verdictsForAnswer(answer, verdicts) {
  const a = norm(answer);
  if (!a) return [];
  return (verdicts || []).filter((v) => {
    const q = norm(v.quote).slice(0, 60);
    return q.length >= 12 && a.includes(q);
  });
}

function LandingTag({ landing }) {
  return (
    <span className="font-mono text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">
      {landing}
    </span>
  );
}

function RoleTag({ role }) {
  if (!role) return null;
  const buyer = role === "buyer";
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${buyer ? "bg-amber-500/15 text-amber-300" : "bg-blue-500/15 text-blue-300"}`}>
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-xs px-3 py-1 rounded-full border transition whitespace-nowrap ${
        active
          ? "bg-blue-500/15 border-transparent text-blue-300"
          : "border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600"
      }`}
    >
      {children}
    </button>
  );
}

function ConversationRow({ c, selected, onSelect }) {
  const high = (c.verdicts || []).some((v) => v.severity === "high");
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 border-b border-zinc-800 transition ${
        selected ? "bg-blue-500/10" : "hover:bg-zinc-800/50"
      }`}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <LandingTag landing={c.landing} />
        <span className="text-sm font-medium text-zinc-100 truncate max-w-[9rem]">{whoOf(c)}</span>
        <RoleTag role={c.role} />
        {c.has_contact && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">lead</span>}
        {high && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-500/15 text-red-300">flagged</span>}
        {c.review?.reviewed && (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" aria-label="Reviewed" title={c.review.note ? `Reviewed: ${c.review.note}` : "Reviewed"} />
        )}
        <span className="ml-auto text-[11px] text-zinc-500 tabular-nums">{fmtShort(c.last_at)}</span>
      </div>
      <div className="text-xs text-zinc-400 truncate mt-1">{c.first_question || "—"}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5 tabular-nums">
        {c.turns} {c.turns === 1 ? "turn" : "turns"} · {fmtDuration(c.started_at, c.last_at) || "—"}
      </div>
    </button>
  );
}

function Bubble({ side, children, flagged }) {
  const base = "max-w-[82%] px-3 py-2 rounded-xl text-[13px] leading-relaxed whitespace-pre-wrap break-words";
  if (side === "user") {
    return <div className={`${base} self-end bg-zinc-800 border border-zinc-700 text-zinc-100`}>{children}</div>;
  }
  return (
    <div className={`${base} self-start bg-blue-500/10 text-zinc-100 ${flagged ? "ring-1 ring-red-400/70 ring-offset-2 ring-offset-zinc-900" : ""}`}>
      {children}
    </div>
  );
}

function KV({ rows }) {
  const shown = rows.filter(([, v]) => v);
  if (!shown.length) return <div className="text-xs text-zinc-500">—</div>;
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1.5 text-xs">
      {shown.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-zinc-500">{k}</dt>
          <dd className="text-zinc-200 break-words min-w-0">{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function PanelSection({ icon: Icon, title, children }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {title}
      </h3>
      {children}
    </section>
  );
}

// Manual review: a note on what went wrong and a "reviewed" mark. Saving a note
// marks the conversation reviewed and moves on to the next unreviewed one, so a
// queue can be worked through top to bottom without going back to the list.
function ReviewBox({ review, onSave }) {
  const [note, setNote] = useState(review?.note || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setNote(review?.note || ""); setError(""); }, [review?.note, review?.reviewed_at]);

  const save = async (reviewed) => {
    setSaving(true);
    setError("");
    try {
      await onSave({ note, reviewed });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        <NotebookPen className="w-3.5 h-3.5" /> Review
        {review?.reviewed && <span className="ml-auto normal-case tracking-normal font-normal text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> reviewed</span>}
      </h3>
      <label htmlFor="chat-review-note" className="sr-only">Review note</label>
      <textarea
        id="chat-review-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") save(true); }}
        rows={4}
        placeholder="What went wrong here? What should the bot have said?"
        className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-2.5 py-2 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-blue-500 resize-y"
      />
      <div className="flex flex-wrap gap-1.5">
        <button type="button" disabled={saving} onClick={() => save(true)}
          className="text-xs px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-60">
          {saving ? "Saving…" : note.trim() ? "Save · next" : "No issues · next"}
        </button>
        {review?.reviewed && (
          <button type="button" disabled={saving} onClick={() => save(false)}
            className="text-xs px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-60">
            Reopen
          </button>
        )}
      </div>
      {review?.reviewed && review.reviewed_by && (
        <div className="text-[11px] text-zinc-500">by {review.reviewed_by}{review.reviewed_at ? ` · ${review.reviewed_at.slice(0, 10)}` : ""}</div>
      )}
      {error && <div className="text-xs text-red-300">{error}</div>}
      <div className="text-[11px] text-zinc-600">⌘/Ctrl + Enter to save</div>
    </section>
  );
}

function CaseFile({ detail, onSaveReview }) {
  const lead = detail.lead || {};
  const meta = detail.meta || {};
  const verdicts = detail.verdicts || [];
  const events = detail.events || [];
  const silenced = events.some((e) => e.type === "GIBBERISH_STOP");
  const crmPushes = events.filter((e) => e.type === "CRM_LEAD_PUSH");
  const crmOk = crmPushes.some((e) => e.raw?.result?.ok);
  return (
    <div className="p-4 space-y-5">
      <ReviewBox review={detail.review} onSave={onSaveReview} />

      <PanelSection icon={UserRound} title="Lead">
        <KV rows={[
          ["Who", ROLE_LABEL[lead.role] || lead.role],
          ["Name", lead.name],
          ["Phone", lead.phone],
          ["Email", lead.email],
        ]} />
        {!lead.phone && !lead.email && <div className="text-xs text-zinc-500">No contact left in this conversation.</div>}
        {crmPushes.length > 0 && !detail.crm?.deal_id && (
          <div className={`text-xs ${crmOk ? "text-emerald-300" : "text-amber-300"}`}>
            {crmOk ? "Delivered to CRM" : "CRM push did not confirm — check the CRM"}
          </div>
        )}
        {detail.crm?.deal_id && (
          <a href={detail.crm.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-zinc-700 text-blue-300 hover:bg-zinc-800">
            <ExternalLink className="w-3.5 h-3.5" /> Open deal in CRM
            {detail.crm.owner && <span className="text-zinc-500">· {detail.crm.owner}</span>}
          </a>
        )}
      </PanelSection>

      {detail.form_lead?.time && (
        <PanelSection icon={FileText} title="Form submission">
          <KV rows={[
            ["When", detail.form_lead.time],
            ["Name", detail.form_lead.name],
            ["Phone", detail.form_lead.phone],
            ["Email", detail.form_lead.email],
            ["Company", detail.form_lead.company],
            ["Who", ROLE_LABEL[detail.form_lead.role] || detail.form_lead.role],
          ]} />
          <div className="text-[11px] text-zinc-500">The same visitor also submitted the form — that is where the contact came from.</div>
        </PanelSection>
      )}

      <PanelSection icon={Megaphone} title="Source">
        <KV rows={[
          ["City", meta.geo],
          ["Landing", LANDING_HOST[detail.landing] || detail.landing],
          ["Ad", meta.ad_source],
          ["Campaign", meta.utm_campaign],
          ["Content", meta.utm_content],
          ["Term", meta.utm_term],
        ]} />
      </PanelSection>

      <PanelSection icon={ShieldAlert} title="Judge">
        {verdicts.length === 0 ? (
          <div className="text-xs text-zinc-500">No findings.</div>
        ) : (
          <div className="space-y-2">
            {verdicts.map((v) => (
              <div
                key={v.code}
                className={`rounded-lg px-3 py-2 text-xs border ${
                  v.severity === "high"
                    ? "bg-red-500/10 border-red-500/20 text-zinc-200"
                    : "bg-zinc-800/60 border-zinc-700 text-zinc-300"
                }`}
              >
                <span className={`font-mono font-semibold ${v.severity === "high" ? "text-red-300" : "text-zinc-400"}`}>{v.code}</span>
                {v.severity !== "high" && <span className="text-zinc-500"> · minor</span>}
                <div className="mt-1">{v.why}</div>
              </div>
            ))}
          </div>
        )}
        {silenced && <div className="text-xs text-amber-300">The bot went silent here after repeated unclear messages.</div>}
      </PanelSection>
    </div>
  );
}

export default function MarketingChatsPage() {
  const [params, setParams] = useSearchParams();
  const landing = params.get("landing") || "all";
  const dateFrom = params.get("from") || isoDaysAgo(30);
  const dateTo = params.get("to") || isoDaysAgo(0);
  const hasContact = params.get("contact") === "1";
  const flagged = params.get("flagged") === "1";
  const engaged = params.get("engaged") === "1";
  const notReviewed = params.get("todo") === "1";
  const selLanding = params.get("c_landing");
  const selSession = params.get("c_session");

  const [q, setQ] = useState(params.get("q") || "");
  const [list, setList] = useState({ total: 0, items: [] });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const setParam = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "" || v === false) next.delete(k);
      else next.set(k, v === true ? "1" : String(v));
    });
    setParams(next, { replace: true });
  }, [params, setParams]);

  // Debounced search → URL
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get("q") || "") !== q) setParam({ q });
    }, 350);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryKey = useMemo(
    () => JSON.stringify({ landing, dateFrom, dateTo, hasContact, flagged, engaged, notReviewed, q: params.get("q") || "" }),
    [landing, dateFrom, dateTo, hasContact, flagged, engaged, notReviewed, params]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setListLoading(true);
      setListError("");
      try {
        const res = await api.get("/landing-leads/chats", {
          params: {
            landing,
            date_from: dateFrom,
            date_to: dateTo,
            has_contact: hasContact ? true : undefined,
            flagged: flagged || undefined,
            min_turns: engaged ? 3 : 1,
            reviewed: notReviewed ? false : undefined,
            q: params.get("q") || undefined,
            limit: 200,
          },
        });
        if (!cancelled) setList(res.data || { total: 0, items: [] });
      } catch (e) {
        if (!cancelled) setListError(e?.response?.data?.detail || "Couldn't load conversations.");
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the first conversation on wide screens when nothing is selected, so the
  // page lands in a working state instead of an empty right side.
  useEffect(() => {
    if (selSession || !list.items.length) return;
    if (window.matchMedia && window.matchMedia("(min-width: 1024px)").matches) {
      const first = list.items[0];
      setParam({ c_landing: first.landing, c_session: first.session });
    }
  }, [list.items]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selLanding || !selSession) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const res = await api.get(`/landing-leads/chats/${encodeURIComponent(selLanding)}/${encodeURIComponent(selSession)}`);
        if (!cancelled) setDetail(res.data);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selLanding, selSession]);

  const openConversation = (c) => setParam({ c_landing: c.landing, c_session: c.session });

  const saveReview = async ({ note, reviewed }) => {
    const res = await api.put(
      `/landing-leads/chats/${encodeURIComponent(selLanding)}/${encodeURIComponent(selSession)}/review`,
      { note, reviewed }
    );
    const review = res.data;
    setDetail((d) => (d ? { ...d, review } : d));
    const items = list.items.map((c) => (c.landing === selLanding && c.session === selSession ? { ...c, review } : c));
    setList((l) => ({ ...l, items }));
    if (!reviewed) return;
    const idx = items.findIndex((c) => c.landing === selLanding && c.session === selSession);
    const next = items.slice(idx + 1).find((c) => !c.review?.reviewed) || items.slice(0, idx).find((c) => !c.review?.reviewed);
    if (next) openConversation(next);
  };
  const closeConversation = () => setParam({ c_landing: null, c_session: null });

  const selectedSummary = list.items.find((c) => c.landing === selLanding && c.session === selSession);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0.5 overflow-x-auto">
        <Link to="/marketing-analytics" className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition">Overview</Link>
        <Link to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Users className="w-3 h-3" /> People
        </Link>
        <Link to={`/marketing-analytics/sales?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Sales scoring
        </Link>
        <Link to={`/marketing-analytics/landings?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1">
          <Globe2 className="w-3 h-3" /> Landings & Leads
        </Link>
        <Link to={`/marketing-analytics/chats?from=${dateFrom}&to=${dateTo}`} className="px-4 py-2 text-sm text-blue-400 font-medium border-b-2 border-blue-500 -mb-0.5 inline-flex items-center gap-1">
          <MessagesSquare className="w-3 h-3" /> Chats
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
          <MessagesSquare className="w-6 h-6 text-blue-400" /> Chats
        </h1>
        <p className="text-zinc-400 text-sm mt-1 max-w-3xl">
          Conversations with the AI assistant on all five landings, read as dialogues — with the lead, the traffic source and the quality judge's findings next to each one.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {LANDINGS.map((l) => (
            <Chip key={l.key} active={landing === l.key} onClick={() => setParam({ landing: l.key === "all" ? null : l.key, c_landing: null, c_session: null })}>
              {l.label}
            </Chip>
          ))}
        </div>
        <span className="w-px h-5 bg-zinc-800 mx-1 hidden sm:block" />
        <Chip active={hasContact} onClick={() => setParam({ contact: !hasContact })}>With contact</Chip>
        <Chip active={flagged} onClick={() => setParam({ flagged: !flagged })}>Flagged</Chip>
        <Chip active={engaged} onClick={() => setParam({ engaged: !engaged })}>3+ turns</Chip>
        <Chip active={notReviewed} onClick={() => setParam({ todo: !notReviewed })}>Not reviewed</Chip>
        <div className="flex items-center gap-1.5 text-xs text-zinc-400">
          <label htmlFor="chats-from" className="sr-only">From</label>
          <input id="chats-from" type="date" value={dateFrom} max={dateTo}
            onChange={(e) => setParam({ from: e.target.value })}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-zinc-200" />
          <span>–</span>
          <label htmlFor="chats-to" className="sr-only">To</label>
          <input id="chats-to" type="date" value={dateTo} min={dateFrom}
            onChange={(e) => setParam({ to: e.target.value })}
            className="bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-zinc-200" />
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input id="chats-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages, phone, city…"
            className="pl-8 h-8 bg-zinc-900 border-zinc-700 text-sm" />
        </div>
      </div>

      {/* min-h-0 на колонках обязателен: у grid- и flex-детей min-height по умолчанию auto,
          колонка растягивается под содержимое, и overflow-y-auto внутри неё не прокручивается —
          список и переписка просто обрезались родительским overflow-hidden. */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden lg:grid lg:grid-cols-[300px_minmax(0,1fr)_280px] lg:h-[calc(100vh-15rem)] lg:min-h-[520px]">
        {/* List */}
        <div className={`border-zinc-800 lg:border-r min-w-0 min-h-0 flex flex-col ${selSession ? "hidden lg:flex" : "flex"}`}>
          <div className="px-3 py-2 text-[11px] text-zinc-500 border-b border-zinc-800 tabular-nums">
            {listLoading ? "Loading…" : `${list.total} ${list.total === 1 ? "conversation" : "conversations"}`}
            {!listLoading && list.total > list.items.length && ` · showing ${list.items.length}`}
          </div>
          <div className="overflow-y-auto flex-1 min-h-0 max-h-[70vh] lg:max-h-none">
            {listLoading && (
              <div className="flex items-center justify-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
            {!listLoading && listError && <div className="p-4 text-sm text-red-300">{listError}</div>}
            {!listLoading && !listError && list.items.length === 0 && (
              <div className="p-6 text-sm text-zinc-400">
                No conversations match these filters. Widen the dates or clear a filter.
              </div>
            )}
            {!listLoading && list.items.map((c) => (
              <ConversationRow
                key={`${c.landing}|${c.session}`}
                c={c}
                selected={c.landing === selLanding && c.session === selSession}
                onSelect={() => openConversation(c)}
              />
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className={`min-w-0 min-h-0 flex flex-col lg:border-r border-zinc-800 ${selSession ? "flex" : "hidden lg:flex"}`}>
          {!selSession ? (
            <div className="flex-1 flex items-center justify-center p-8 text-sm text-zinc-500">Pick a conversation on the left.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-zinc-800">
                <button type="button" onClick={closeConversation} className="lg:hidden text-zinc-400 hover:text-zinc-100 -ml-1 p-1" aria-label="Back to the list">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <LandingTag landing={selLanding} />
                <span className="font-semibold text-zinc-100">{whoOf(detail ? { lead: detail.lead, geo: detail.meta?.geo } : selectedSummary)}</span>
                <RoleTag role={detail?.lead?.role || selectedSummary?.role} />
                {detail && (detail.lead?.phone || detail.lead?.email) && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">lead</span>
                )}
                {detail && (
                  <span className="text-xs text-zinc-500 inline-flex items-center gap-1 tabular-nums">
                    <Clock className="w-3 h-3" /> {fmtFull(detail.started_at)} · {detail.turns.length} turns · {fmtDuration(detail.started_at, detail.last_at)}
                  </span>
                )}
                <button type="button" disabled
                  title="Live takeover through Telegram — coming in the next step"
                  className="ml-auto text-xs border border-zinc-700 text-zinc-500 rounded-md px-3 py-1.5 inline-flex items-center gap-1.5 cursor-not-allowed">
                  <Hand className="w-3.5 h-3.5" /> Take over · soon
                </button>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0 p-4 flex flex-col gap-2 max-h-[70vh] lg:max-h-none">
                {detailLoading && (
                  <div className="flex items-center justify-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
                )}
                {!detailLoading && !detail && <div className="text-sm text-zinc-400">Couldn't load this conversation.</div>}
                {!detailLoading && detail && detail.turns.map((t, i) => {
                  const hits = verdictsForAnswer(t.answer, detail.verdicts);
                  return (
                    <React.Fragment key={i}>
                      {t.question && <Bubble side="user">{t.question}</Bubble>}
                      {t.answer && <Bubble side="bot" flagged={hits.some((v) => v.severity === "high")}>{t.answer}</Bubble>}
                      {hits.map((v) => (
                        <div key={v.code} className="self-start text-[11px] text-red-300 inline-flex items-center gap-1 -mt-1">
                          <Flag className="w-3 h-3" /> {v.code} · {v.why}
                        </div>
                      ))}
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Case file */}
        <div className={`min-w-0 min-h-0 overflow-y-auto border-t lg:border-t-0 border-zinc-800 ${selSession ? "block" : "hidden lg:block"}`}>
          {detail ? <CaseFile detail={detail} onSaveReview={saveReview} /> : (
            <div className="p-4 text-xs text-zinc-500 space-y-3">
              <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Lead, traffic source and judge findings appear here.</div>
              <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /><Mail className="w-3.5 h-3.5" /> Contacts left in the chat are assembled across the whole conversation.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
